// Routes

"use strict";

import { createRouter, createWebHistory, Router, RouteRecordRaw } from "vue-router";
import { Timeouts } from "./utils/timeout";
import { AppEvents } from "./control/app-events";

// Router
// https://router.vuejs.org/guide/#javascript

// List of forbidden routes:
//
//    - /api/*  - This is reserved for the API
//    - /static/* - This is reserved for static assets
//    - /webhooks*/ - Reserved for webhooks

const routes: (RouteRecordRaw & {
    meta?: {
        /**
         * Set it to true for sticky sidebar
         */
        sidebarSticky?: boolean;
    };
})[] = [
    {
        name: "landing-page",
        path: "/",
        component: () => import("@/components/routes/LandingPage.vue"),
         meta: {
            hideTopBar: true
        },
    },

    {
        name: "home",
        path: "/home",
        component: () => import("@/components/routes/HomePage.vue"),
        meta: {
            title: "Home",
        },
    },

    {
        name: "services",
        path: "/services",
        component: () => import("@/components/routes/ServicesPage.vue"),
        meta: {
            title: "Services",
        },
    },

    {
        name: "favourite-services",
        path: "/favourite-services",
        component: () => import("@/components/routes/FavouriteServicesPage.vue"),
        meta: {
            title: "Favourite services",
        },
    },

    {
        name: "payment-orders",
        path: "/payment-orders",
        component: () => import("@/components/routes/PaymentsOrdersPage.vue"),
        meta: {
            title: "Payment orders",
        },
    },

    {
        path: "/services/:id",
        name: "ServicePage",
        component: () => import("@/components/routes/ServicePage.vue"),
        meta: {
            title: "Service",
        },
    },

    {
        name: "use-cases",
        path: "/use-cases",
        component: () => import("@/components/routes/UseCasesPage.vue"),
        meta: {
            title: "Use cases",
        },
    },

     {
        name: "use-case",
        path: "/use-cases/:id",
        component: () => import("@/components/routes/UseCasePage.vue"),
        meta: {
            title: "Use case",
        },
    },

    {
        name: "applications",
        path: "/applications",
        component: () => import("@/components/routes/ApplicationsPage.vue"),
        meta: {
            title: "Applications",
        },
    },

    {
        name: "data-space",
        path: "/data-space",
        component: () => import("@/components/routes/data-space/DataSpacePage.vue"),
        meta: {
            title: "Data space",
        },
    },
    {
        name: "data-space-details",
        path: "/data-space/:id",
        component: () => import("@/components/routes/data-space/DataSpaceDetailsPage.vue"),
        meta: {
            title: "Data space details",
        },
    },
    {
        name: "terms",
        path: "/terms",
        component: () => import("@/components/routes/TermsOfUsePage.vue"),
        meta: {
            title: "Terms"
        },
    },

    {
        name: "cookies",
        path: "/cookies",
        component: () => import("@/components/routes/CookiePolicyPage.vue"),
        meta: {
            title: "Cookies"
        },
    },

    {
        name: "privacy",
        path: "/privacy",
        component: () => import("@/components/routes/PrivacyPolicyPage.vue"),
        meta: {
            title: "Privacy"
        },
    },

    /* Auth */

    {
        name: "login",
        path: "/login",
        component: () => import("@/components/routes/auth/LoginPage.vue"),
        meta: {
            title: "Login",
            hideTopBar: true
        },
    },

    {
        name: "tfa-login",
        path: "/login/tfa",
        component: () => import("@/components/routes/auth/TwoFactorLoginPage.vue"),
        meta: {
            title: "TFA Login"
        },
    },

    {
        name: "signup",
        path: "/signup",
        component: () => import("@/components/routes/auth/SignupPage.vue"),
        meta: {
            title: "Signup",
            hideTopBar: true
        },
    },
    {
        name: "signup-success",
        path: "/signup/success",
        component: () => import("@/components/routes/auth/SignupSuccessPage.vue"),
        meta: {
            title: "Signup success"
        },
    },

    {
        name: "forgot-password",
        path: "/password/forgot",
        component: () => import("@/components/routes/auth/ForgotPasswordPage.vue"),
        meta: {
            title: "Forgot password",
            hideTopBar: true
        },
    },
    {
        name: "reset-password",
        path: "/password/reset/:uid/:token",
        component: () => import("@/components/routes/auth/ResetPasswordPage.vue"),
        meta: {
            title: "Reset password"
        },
    },

    {
        name: "verify-email",
        path: "/email/verify/:uid/:token",
        component: () => import("@/components/routes/auth/EmailVerifyPage.vue"),
        meta: {
            title: "Verify email"
        },
    },

    {
        name: "tp-login",
        path: "/login/tp/:service",
        component: () => import("@/components/routes/auth/ThirdPartyLogin.vue"),
        meta: {
            title: "Tp Login"
        },
    },

    {
        name: "tp-signup",
        path: "/signup/tp",
        component: () => import("@/components/routes/auth/ThirdPartySignupPage.vue"),
        meta: {
            title: "Tp Signup"
        },
    },

    /* Profile */

    {
        name: "profile",
        path: "/user/:username",
        component: () => import("@/components/routes/profile/ProfilePage.vue"),
        meta: {
            title: "Profile"
        },
    },

    /* Account */

    {
        name: "account-settings",
        path: "/account-settings",
        component: () => import("@/components/routes/account/AccountSettingsPage.vue"),
        meta: {
            title: "Account settings"
        },
    },

    /* Admin */

    {
        name: "admin",
        path: "/admin",
        component: () => import("@/components/routes/admin/AdministrationPage.vue"),
        meta: {
            title: "Admin page"
        },
    },

    {
        name: "admin-user",
        path: "/admin/users/:id",
        component: () => import("@/components/routes/admin/UserPage.vue"),
        meta: {
            title: "User page"
        },
    },

    /* Default */

    {
        path: "/:catchAll(.*)",
        component: () => import("@/components/routes/NotFoundPage.vue"),
    },
];

export function makeApplicationRouter(): Router {
    const router = createRouter({
        // 4. Provide the history implementation to use. We are using the hash history for simplicity here.
        history: createWebHistory(),
        routes, // short for `routes: routes`
    });

    router.beforeEach(() => {
        Timeouts.Set("router-load-state", 300, () => {
            AppEvents.Emit("router-load-state-change", true);
        });
    });

    router.afterEach(() => {
        Timeouts.Abort("router-load-state");
        AppEvents.Emit("router-load-state-change", false);
    });

    return router;
}
